import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from './supabase';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Filler } from 'chart.js';
import { Pie, Bar, Line, Doughnut } from 'react-chartjs-2';
import GhanaHeatmap from './GhanaHeatmap';
import './App.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Filler);

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ghanaRed = '#CC0000';
const ghanaGreen = '#006B3F';
const ghanaGold = '#FFD700';
const ghanaBlack = '#000000';

const ghanaRegions = [
  'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Central',
  'Volta', 'Northern', 'Upper East', 'Upper West', 'Bono',
  'Ahafo', 'Bono East', 'Oti', 'North East', 'Savannah', 'Western North'
];

const areaToConstituency = {
  'spintex': 'Ayawaso East', 'madina': 'La Nkwantanang-Madina', 'adenta': 'Adenta',
  'tema': 'Tema Central', 'ashaiman': 'Ashaiman', 'nkawkaw': 'Nkawkaw',
  'kumasi': 'Asokwa', 'accra central': 'Ayawaso Central', 'osu': 'Klottey Korle',
  'labone': 'La Dade Kotopon', 'dansoman': 'Ablekuma South', 'koforidua': 'New Juaben South',
  'sunyani': 'Sunyani East', 'takoradi': 'Takoradi', 'ho': 'Ho Central',
  'cape coast': 'Cape Coast South', 'tamale': 'Tamale Central', 'bolgatanga': 'Bolgatanga Central', 'wa': 'Wa Central'
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
};

const QUEUE_KEY = 'civicconnect_offline_queue';
const getQueue = () => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; } };
const saveQueue = (q) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

const getLocationName = async (lat, lng) => {
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`);
    const data = await res.json();
    return data.display_name ? data.display_name.substring(0, 100) : `${lat}, ${lng}`;
  } catch { return `${lat}, ${lng}`; }
};

const getConstituencyFromLocation = async (locationText, lat, lng, selectedRegion) => {
  if (!locationText && !lat) return { constituency: null, region: selectedRegion, isDCE: true };
  const locationLower = (locationText || '').toLowerCase();
  for (const [area, constituency] of Object.entries(areaToConstituency)) {
    if (locationLower.includes(area)) {
      const { data: constData } = await supabase.from('constituencies').select('region').eq('name', constituency).single();
      return { constituency, region: constData?.region || selectedRegion, isDCE: false };
    }
  }
  const { data: constituencies } = await supabase.from('constituencies').select('name, region');
  if (constituencies) {
    for (const c of constituencies) {
      if (locationLower.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(locationLower)) {
        return { constituency: c.name, region: c.region, isDCE: false };
      }
    }
  }
  return { constituency: null, region: selectedRegion, isDCE: true };
};

function CitizenLocationMarker({ setLocationLatLng, setLocationLat, setLocationLng, setLocationName }) {
  const [position, setPosition] = useState(null);
  useMapEvents({
    async click(e) {
      const { lat, lng } = e.latlng;
      setPosition(e.latlng);
      setLocationLatLng(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      setLocationLat(lat.toFixed(6));
      setLocationLng(lng.toFixed(6));
      const name = await getLocationName(lat, lng);
      setLocationName(name);
    },
  });
  return position === null ? null : <Marker position={position} />;
}

function EmployeeLocationMarker({ setLocationLat, setLocationLng, setLocationName, setLocationHome }) {
  const [position, setPosition] = useState(null);
  useMapEvents({
    async click(e) {
      const { lat, lng } = e.latlng;
      setPosition(e.latlng);
      setLocationLat(lat.toFixed(6));
      setLocationLng(lng.toFixed(6));
      const name = await getLocationName(lat, lng);
      setLocationName(name);
      setLocationHome(name);
    },
  });
  return position === null ? null : <Marker position={position} />;
}

function PoliceLocationMarker({ setLocationLat, setLocationLng, setLocationName, setLocation }) {
  const [position, setPosition] = useState(null);
  useMapEvents({
    async click(e) {
      const { lat, lng } = e.latlng;
      setPosition(e.latlng);
      if (setLocationLat) setLocationLat(lat.toFixed(6));
      if (setLocationLng) setLocationLng(lng.toFixed(6));
      const name = await getLocationName(lat, lng);
      if (setLocationName) setLocationName(name);
      if (setLocation) setLocation(name);
    },
  });
  return position === null ? null : <Marker position={position} />;
}

function App() {
  const [activeTab, setActiveTab] = useState('citizen');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState(getQueue());

  // Citizen
  const [issueType, setIssueType] = useState('Drainage / Flooding');
  const [otherExplanation, setOtherExplanation] = useState('');
  const [description, setDescription] = useState('');
  const [locationLatLng, setLocationLatLng] = useState('');
  const [locationLat, setLocationLat] = useState('');
  const [locationLng, setLocationLng] = useState('');
  const [locationName, setLocationName] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [urgencyLevel, setUrgencyLevel] = useState('Medium');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [mapCenter, setMapCenter] = useState([5.6037, -0.1870]);
  const [isLocating, setIsLocating] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([{ id: 0, file: null }, { id: 1, file: null }]);
  const [photoPreviews, setPhotoPreviews] = useState([null, null]);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [trackPhone, setTrackPhone] = useState('');
  const [trackResults, setTrackResults] = useState([]);
  const [trackLoading, setTrackLoading] = useState(false);

  // Police
  const [policePassword, setPolicePassword] = useState('');
  const [policeLoggedIn, setPoliceLoggedIn] = useState(false);
  const [policeOfficerName, setPoliceOfficerName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [plateQuery, setPlateQuery] = useState('');
  const [plateResult, setPlateResult] = useState(null);
  const [crimeType, setCrimeType] = useState('Theft/Robbery');
  const [crimeOtherText, setCrimeOtherText] = useState('');
  const [crimeDescription, setCrimeDescription] = useState('');
  const [crimeLocation, setCrimeLocation] = useState('');
  const [crimeRegion, setCrimeRegion] = useState('Select region');
  const [suspectName, setSuspectName] = useState('');
  const [suspectPlate, setSuspectPlate] = useState('');
  const [fineAmountCrime, setFineAmountCrime] = useState(0);
  const [fineReason, setFineReason] = useState('');
  const [courtDate, setCourtDate] = useState('');
  const [crimePhoto, setCrimePhoto] = useState(null);
  const [crimePhotoPreview, setCrimePhotoPreview] = useState(null);
  const [parkingLocation, setParkingLocation] = useState('');
  const [parkingRegion, setParkingRegion] = useState('Select region');
  const [parkingNumberPlate, setParkingNumberPlate] = useState('');
  const [violationType, setViolationType] = useState('Parking on pavement/sidewalk');
  const [violationOtherText, setViolationOtherText] = useState('');
  const [fineAmountParking, setFineAmountParking] = useState(150);
  const [parkingPhoto, setParkingPhoto] = useState(null);
  const [parkingPhotoPreview, setParkingPhotoPreview] = useState(null);
  const [crimeLocationLat, setCrimeLocationLat] = useState('');
  const [crimeLocationLng, setCrimeLocationLng] = useState('');
  const [crimeLocationName, setCrimeLocationName] = useState('');
  const [parkingLocationLat, setParkingLocationLat] = useState('');
  const [parkingLocationLng, setParkingLocationLng] = useState('');
  const [parkingLocationName, setParkingLocationName] = useState('');

  // Court
  const [courtPassword, setCourtPassword] = useState('');
  const [courtLoggedIn, setCourtLoggedIn] = useState(false);

  // MP
  const [mpPassword, setMpPassword] = useState('');
  const [mpLoggedIn, setMpLoggedIn] = useState(false);
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [mpReports, setMpReports] = useState([]);
  const [mpStats, setMpStats] = useState({ total: 0, resolved: 0, pending: 0, highUrgency: 0, credits: 0 });
  const [mpCategoryCounts, setMpCategoryCounts] = useState({});

  // Admin
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [adminFilterRegion, setAdminFilterRegion] = useState('All');
  const [adminFilterConstituency, setAdminFilterConstituency] = useState('All');
  const [adminFilterDateFrom, setAdminFilterDateFrom] = useState('');
  const [adminFilterDateTo, setAdminFilterDateTo] = useState('');

  // Employee
  const [employeePassword, setEmployeePassword] = useState('');
  const [employeeLoggedIn, setEmployeeLoggedIn] = useState(false);
  const [employeeName, setEmployeeName] = useState('');
  const [citizenName, setCitizenName] = useState('');
  const [ghanaCard, setGhanaCard] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [employeePhone, setEmployeePhone] = useState('');
  const [locationHome, setLocationHome] = useState('');
  const [what3words, setWhat3words] = useState('');
  const [employeeLocationLat, setEmployeeLocationLat] = useState('');
  const [employeeLocationLng, setEmployeeLocationLng] = useState('');
  const [employeeLocationName, setEmployeeLocationName] = useState('');
  const [employeeRegion, setEmployeeRegion] = useState('');
  const [district, setDistrict] = useState('');
  const [community, setCommunity] = useState('');
  const [occupation, setOccupation] = useState('');
  const [nextOfKinName, setNextOfKinName] = useState('');
  const [nextOfKinPhone, setNextOfKinPhone] = useState('');
  const [employeePhoto, setEmployeePhoto] = useState(null);
  const [employeePhotoPreview, setEmployeePhotoPreview] = useState(null);
  const [employeeMapCenter, setEmployeeMapCenter] = useState([5.6037, -0.1870]);
  const [employeeIsLocating, setEmployeeIsLocating] = useState(false);

  // DB
  const [citizenReports, setCitizenReports] = useState([]);
  const [fines, setFines] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [constituencies, setConstituencies] = useState([]);

  useEffect(() => {
    const goOnline = () => { setIsOnline(true); flushOfflineQueue(); };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  useEffect(() => { loadAllData(); }, []);

  const flushOfflineQueue = async () => {
    const queue = getQueue();
    if (queue.length === 0) return;
    let flushed = 0;
    for (const report of queue) {
      const { error } = await supabase.from('citizen_reports').insert([report]);
      if (!error) flushed++;
    }
    if (flushed > 0) {
      saveQueue([]); setOfflineQueue([]);
      alert(`✅ Back online! ${flushed} queued report(s) submitted.`);
      loadAllData();
    }
  };

  const loadAllData = async () => {
    const { data: rD } = await supabase.from('citizen_reports').select('*').order('created_at', { ascending: false });
    if (rD) setCitizenReports(rD);
    const { data: fD } = await supabase.from('fines').select('*').order('issued_date', { ascending: false });
    if (fD) setFines(fD);
    const { data: eD } = await supabase.from('employee_data').select('*');
    if (eD) setEmployeeData(eD);
    const { data: cD } = await supabase.from('constituencies').select('*');
    if (cD) setConstituencies(cD);
  };

  const uploadFile = async (file, id, folder) => {
    if (!file) return null;
    const ext = file.name.split('.').pop();
    const fileName = `${folder}/${id}.${ext}`;
    const { error } = await supabase.storage.from('civicconnect-photos').upload(fileName, file, { cacheControl: '3600', upsert: false });
    if (error) return null;
    const { data } = supabase.storage.from('civicconnect-photos').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const printFineReceipt = (fine) => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Fine Receipt</title><style>body{font-family:Arial;padding:40px}.header{text-align:center;border-bottom:2px solid #CC0000}.amount{font-size:28px;color:#CC0000;font-weight:bold}</style></head><body><div class="header"><h1>CivicConnect Ghana</h1><p>Police Service - Fine Receipt</p></div><p><strong>Fine ID:</strong> ${fine.fine_id}</p><p><strong>Offender:</strong> ${fine.offender_name || 'Unknown'}</p><p><strong>Violation:</strong> ${fine.violation_type}</p><p><strong>Location:</strong> ${fine.location}</p><p><strong>Amount:</strong> GH¢ ${fine.fine_amount}</p><p><strong>Court Date:</strong> ${fine.court_date || 'To be scheduled'}</p><div class="amount">Pay within 14 days</div></body></html>`);
    w.print();
  };

  const urgencyBadge = (level) => {
    const colors = { High: '#e74c3c', Medium: '#e67e22', Low: '#27ae60' };
    return <span style={{ backgroundColor: colors[level] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', marginLeft: '4px' }}>{level || 'N/A'}</span>;
  };

  const detectCitizenLocation = () => {
    setIsLocating(true);
    if (!navigator.geolocation) { alert('Location not supported'); setIsLocating(false); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      setMapCenter([latitude, longitude]);
      setLocationLatLng(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      setLocationLat(latitude.toFixed(6));
      setLocationLng(longitude.toFixed(6));
      const name = await getLocationName(latitude, longitude);
      setLocationName(name);
      setIsLocating(false);
      alert(`Location detected: ${name}`);
    }, () => { setIsLocating(false); alert('Could not detect location'); });
  };

  const addPhotoField = () => { setPhotoFiles([...photoFiles, { id: photoFiles.length, file: null }]); setPhotoPreviews([...photoPreviews, null]); };
  const removePhotoField = (index) => { if (index < 2) return; setPhotoFiles(photoFiles.filter((_, i) => i !== index)); setPhotoPreviews(photoPreviews.filter((_, i) => i !== index)); };
  const handlePhotoChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const nf = [...photoFiles]; nf[index] = { ...nf[index], file }; setPhotoFiles(nf);
      const np = [...photoPreviews]; np[index] = URL.createObjectURL(file); setPhotoPreviews(np);
    }
  };

  const handleTrackReport = async () => {
    if (!trackPhone.trim()) { alert('Enter your phone number'); return; }
    setTrackLoading(true);
    const { data } = await supabase.from('citizen_reports').select('*').eq('phone_number', trackPhone.trim()).order('created_at', { ascending: false });
    setTrackResults(data || []);
    setTrackLoading(false);
    if (!data || data.length === 0) alert('No reports found for this phone number.');
  };

  const resetCitizenForm = () => {
    setDescription(''); setLocationLatLng(''); setLocationLat(''); setLocationLng('');
    setLocationName(''); setPhoneNumber(''); setSelectedVideo(null); setVideoPreview(null);
    setOtherExplanation(''); setUrgencyLevel('Medium');
    setPhotoFiles([{ id: 0, file: null }, { id: 1, file: null }]); setPhotoPreviews([null, null]);
    const vi = document.getElementById('videoInput'); if (vi) vi.value = '';
  };

  const handleCitizenSubmit = async () => {
    if (!phoneNumber) { alert('Phone number is required'); return; }
    if (!selectedRegion) { alert('Please select your region'); return; }
    if (!locationLatLng && !locationName) { alert('Please detect location or click on the map'); return; }
    const validPhotos = photoFiles.filter(p => p.file !== null);
    if (validPhotos.length < 2) { alert('Please upload at least 2 photos'); return; }

    const reportId = `RPT-${Date.now()}`;
    const finalIssueType = issueType === 'Other' ? `Other: ${otherExplanation}` : issueType;
    const { constituency, region, isDCE } = await getConstituencyFromLocation(locationName, locationLat, locationLng, selectedRegion);
    const finalConstituency = constituency || (isDCE ? `DCE-${selectedRegion}` : 'Unknown');

    const reportData = {
      report_id: reportId, issue_type: finalIssueType, urgency: urgencyLevel, description,
      location: locationName, location_lat: locationLat, location_lng: locationLng,
      constituency: finalConstituency, region: region || selectedRegion, selected_region: selectedRegion,
      phone_number: phoneNumber, photo_url: null, additional_photos: [], video_url: null,
      status: 'Pending', created_at: new Date().toISOString(),
    };

    if (!isOnline) {
      const q = getQueue(); q.push(reportData); saveQueue(q); setOfflineQueue(q);
      alert(`📵 Offline. Report saved locally.\nID: ${reportId}\nWill submit when you reconnect.`);
      resetCitizenForm(); return;
    }

    const photoUrls = [];
    for (let i = 0; i < photoFiles.length; i++) {
      if (photoFiles[i].file) { const url = await uploadFile(photoFiles[i].file, `${reportId}_${i}`, 'citizen-photos'); if (url) photoUrls.push(url); }
    }
    let videoUrl = null;
    if (selectedVideo) videoUrl = await uploadFile(selectedVideo, `${reportId}_video`, 'citizen-videos');
    reportData.photo_url = photoUrls[0] || null;
    reportData.additional_photos = photoUrls.slice(1);
    reportData.video_url = videoUrl;

    const { error } = await supabase.from('citizen_reports').insert([reportData]);
    if (error) { alert('Error: ' + error.message); } else {
      alert(`✅ Report submitted!\nID: ${reportId}\n📍 Routed to: ${finalConstituency}\n🚦 Urgency: ${urgencyLevel}\n💰 5 CREDITS when resolved!`);
      await loadAllData(); resetCitizenForm();
    }
  };

  const handleCitizenSearch = async () => {
    if (!searchQuery.trim()) { alert('Enter a name, Ghana Card, or phone number'); return; }
    const query = searchQuery.toLowerCase().trim();
    const results = [];
    const { data: p } = await supabase.from('pseudo_citizens').select('*').or(`name.ilike.%${query}%,gh_card.ilike.%${query}%,phone.ilike.%${query}%`);
    if (p && p.length > 0) results.push(...p.map(x => ({ ...x, source: 'Database', type: 'sample' })));
    const { data: e } = await supabase.from('employee_data').select('*').or(`citizen_name.ilike.%${query}%,ghana_card.ilike.%${query}%,phone_number.ilike.%${query}%`);
    if (e && e.length > 0) results.push(...e.map(x => ({ ...x, source: 'Employee', type: 'real' })));
    setSearchResults(results);
    if (results.length === 0) alert('No records found.');
  };

  const handlePlateLookup = async () => {
    if (!plateQuery.trim()) { alert('Enter plate number'); return; }
    const { data: fR } = await supabase.from('fines').select('*').ilike('offender_plate', `%${plateQuery.toLowerCase().trim()}%`);
    if (fR && fR.length > 0) { setPlateResult(fR[0]); alert(`Found ${fR.length} fine(s) on record`); return; }
    setPlateResult({ plate: plateQuery.toUpperCase(), owner: 'Unknown', make: 'Toyota', outstandingFines: 'GH¢ 0' }); alert('Sample data displayed');
  };

  const detectCrimeLocation = () => {
    if (!navigator.geolocation) { alert('Not supported'); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => { const name = await getLocationName(pos.coords.latitude, pos.coords.longitude); setCrimeLocation(name); alert(`Location: ${name}`); }, () => alert('Could not detect'));
  };

  const handleCrimeSubmit = async () => {
    if (!policeOfficerName) { alert('Enter your name'); return; }
    const finalCrimeType = crimeType === 'Other' ? `Other: ${crimeOtherText}` : crimeType;
    const fineId = `FINE-${Date.now()}`;
    let photoUrl = null;
    if (crimePhoto) photoUrl = await uploadFile(crimePhoto, fineId, 'crime-photos');
    const { error } = await supabase.from('fines').insert([{ fine_id: fineId, offender_name: suspectName || 'Unknown', offender_plate: suspectPlate || 'Unknown', violation_type: finalCrimeType, description: crimeDescription || 'No description', location: crimeLocation || 'Unknown', region: crimeRegion, fine_amount: fineAmountCrime || 0, reason: fineReason, issued_by: policeOfficerName, court_date: courtDate || null, status: 'Unpaid', photo_url: photoUrl }]);
    if (error) { alert('Error: ' + error.message); } else {
      alert(`Fine issued! ID: ${fineId}\nAmount: GH¢ ${fineAmountCrime || 0}`);
      await loadAllData();
      setCrimeDescription(''); setCrimeLocation(''); setSuspectName(''); setSuspectPlate(''); setFineAmountCrime(0); setFineReason(''); setCourtDate(''); setCrimeOtherText(''); setCrimePhoto(null); setCrimePhotoPreview(null);
      const ci = document.getElementById('crimePhotoInput'); if (ci) ci.value = '';
    }
  };

  const detectParkingLocation = () => {
    if (!navigator.geolocation) { alert('Not supported'); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => { const name = await getLocationName(pos.coords.latitude, pos.coords.longitude); setParkingLocation(name); alert(`Location: ${name}`); }, () => alert('Could not detect'));
  };

  const handleParkingSubmit = async () => {
    if (!policeOfficerName) { alert('Enter your name'); return; }
    const finalViolation = violationType === 'Other' ? `Other: ${violationOtherText}` : violationType;
    const fineId = `FINE-${Date.now()}`;
    if (parkingPhoto) await uploadFile(parkingPhoto, fineId, 'parking-photos');
    const { error } = await supabase.from('fines').insert([{ fine_id: fineId, offender_name: 'Vehicle Owner', offender_plate: parkingNumberPlate, violation_type: finalViolation, description: `Parking violation at ${parkingLocation}`, location: parkingLocation, region: parkingRegion, fine_amount: fineAmountParking, reason: 'Parking violation', issued_by: policeOfficerName, status: 'Unpaid' }]);
    if (error) { alert('Error: ' + error.message); } else {
      alert(`Fine issued! ID: ${fineId}\nAmount: GH¢ ${fineAmountParking}`);
      await loadAllData();
      setParkingLocation(''); setParkingNumberPlate(''); setFineAmountParking(150); setViolationOtherText(''); setParkingPhoto(null); setParkingPhotoPreview(null);
      const pi = document.getElementById('parkingPhotoInput'); if (pi) pi.value = '';
    }
  };

  const loadMPReports = async () => {
    if (!selectedConstituency) { alert('Select constituency'); return; }
    const { data } = await supabase.from('citizen_reports').select('*').eq('constituency', selectedConstituency).order('created_at', { ascending: false });
    if (data) {
      setMpReports(data);
      const total = data.length;
      const resolved = data.filter(r => r.status === 'Resolved').length;
      const highUrgency = data.filter(r => r.urgency === 'High').length;
      const { data: iD } = await supabase.from('incentives').select('*').in('report_id', data.map(r => r.report_id));
      const credits = (iD || []).reduce((s, i) => s + (i.amount || 0), 0);
      setMpStats({ total, resolved, pending: total - resolved, highUrgency, credits });
      const cc = {}; data.forEach(r => { cc[r.issue_type] = (cc[r.issue_type] || 0) + 1; }); setMpCategoryCounts(cc);
    }
  };

  const resolveReport = async (reportId, phone) => {
    const afterInput = document.getElementById(`afterPhoto_${reportId}`);
    const afterFile = afterInput?.files[0];
    if (!afterFile) { alert('Please upload an "after" photo'); return; }
    const noteVal = document.getElementById(`resNote_${reportId}`)?.value || '';
    const afterUrl = await uploadFile(afterFile, `${reportId}_after`, 'resolution-photos');
    if (!afterUrl) { alert('Failed to upload photo.'); return; }
    const { error } = await supabase.from('citizen_reports')
      .update({ status: 'Resolved', resolved_by: selectedConstituency, resolved_at: new Date(), after_photo_url: afterUrl, resolution_note: noteVal })
      .eq('report_id', reportId);
    if (error) { alert('Error: ' + error.message); } else {
      await supabase.from('incentives').insert([{ report_id: reportId, phone_number: phone, amount: 5, status: 'Pending' }]);
      alert('✅ Report resolved! Citizen will receive 5 credits.'); loadMPReports(); loadAllData();
    }
  };

  const detectEmployeeLocation = () => {
    setEmployeeIsLocating(true);
    if (!navigator.geolocation) { alert('Not supported'); setEmployeeIsLocating(false); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      setEmployeeMapCenter([latitude, longitude]); setEmployeeLocationLat(latitude.toFixed(6)); setEmployeeLocationLng(longitude.toFixed(6));
      const name = await getLocationName(latitude, longitude);
      setEmployeeLocationName(name); setLocationHome(name); setEmployeeIsLocating(false);
      alert(`Location: ${name}`);
    }, () => { setEmployeeIsLocating(false); alert('Could not detect'); });
  };

  const handleEmployeeSubmit = async () => {
    const consent = document.getElementById('employeeConsentCheckbox');
    if (!consent || !consent.checked) { alert('Please confirm consent from the citizen'); return; }
    if (!employeeName) { alert('Enter your name'); return; }
    const dataId = `EMP-${Date.now()}`;
    let photoUrl = null;
    if (employeePhoto) photoUrl = await uploadFile(employeePhoto, dataId, 'employee-photos');
    const { error } = await supabase.from('employee_data').insert([{ employee_name: employeeName, citizen_name: citizenName || null, ghana_card: ghanaCard || null, date_of_birth: dateOfBirth || null, gender: gender || null, phone_number: employeePhone || null, location_home: locationHome || employeeLocationName || null, location_lat: employeeLocationLat || null, location_lng: employeeLocationLng || null, what3words: what3words || null, photo_url: photoUrl, region: employeeRegion || null, district: district || null, community: community || null, occupation: occupation || null, next_of_kin_name: nextOfKinName || null, next_of_kin_phone: nextOfKinPhone || null, submitted_by: employeeName }]);
    if (error) { alert('Error: ' + error.message); } else {
      alert('Data submitted successfully!'); await loadAllData();
      setCitizenName(''); setGhanaCard(''); setDateOfBirth(''); setGender(''); setEmployeePhone(''); setLocationHome('');
      setWhat3words(''); setEmployeeLocationLat(''); setEmployeeLocationLng(''); setEmployeeLocationName('');
      setEmployeeRegion(''); setDistrict(''); setCommunity(''); setOccupation(''); setNextOfKinName(''); setNextOfKinPhone('');
      setEmployeePhoto(null); setEmployeePhotoPreview(null);
      const ep = document.getElementById('employeePhotoInput'); if (ep) ep.value = '';
    }
  };

  // Admin filters
  const adminConstituencies = ['All', ...new Set(citizenReports.map(r => r.constituency).filter(Boolean))];
  const getFilteredReports = () => citizenReports.filter(r => {
    const rm = adminFilterRegion === 'All' || (r.selected_region || r.region) === adminFilterRegion;
    const cm = adminFilterConstituency === 'All' || r.constituency === adminFilterConstituency;
    const df = adminFilterDateFrom ? new Date(r.created_at) >= new Date(adminFilterDateFrom) : true;
    const dt = adminFilterDateTo ? new Date(r.created_at) <= new Date(adminFilterDateTo + 'T23:59:59') : true;
    return rm && cm && df && dt;
  });
  const filteredReports = getFilteredReports();

  const exportFilteredCSV = () => {
    const rows = [['Report ID', 'Type', 'Urgency', 'Description', 'Location', 'Constituency', 'Region', 'Status', 'Days Open', 'Date']];
    filteredReports.forEach(r => rows.push([r.report_id || '', r.issue_type || '', r.urgency || '', (r.description || '').replace(/,/g, ';'), r.location || '', r.constituency || '', r.region || '', r.status || '', r.status === 'Pending' ? daysSince(r.created_at) : 'Resolved', new Date(r.created_at).toLocaleString()]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url;
    a.download = `civicconnect_${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  // Charts
  const categoryChartData = () => { const c = {}; citizenReports.forEach(r => { c[r.issue_type] = (c[r.issue_type] || 0) + 1; }); return { labels: Object.keys(c).slice(0, 8), datasets: [{ data: Object.values(c).slice(0, 8), backgroundColor: ['#CC0000','#006B3F','#FFD700','#1a3a5c','#e67e22','#8e44ad','#2980b9','#27ae60'] }] }; };
  const regionChartData = () => { const c = {}; citizenReports.forEach(r => { const reg = r.selected_region || r.region || 'Unknown'; c[reg] = (c[reg] || 0) + 1; }); const max = Math.max(...Object.values(c), 1); return { labels: ghanaRegions, datasets: [{ label: 'Reports', data: ghanaRegions.map(r => c[r] || 0), backgroundColor: ghanaRegions.map(r => { const v = c[r] || 0; return `rgba(0,107,63,${(0.2 + (v / max) * 0.8).toFixed(2)})`; }) }] }; };
  const constituencyChartData = () => { const c = {}; citizenReports.forEach(r => { const cons = r.constituency || 'Unknown'; c[cons] = (c[cons] || 0) + 1; }); const s = Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 10); return { labels: s.map(x => x[0]), datasets: [{ label: 'Reports', data: s.map(x => x[1]), backgroundColor: '#2980b9' }] }; };
  const timelineChartData = () => { const d7 = [...Array(7)].map((_, i) => { const d = new Date(); d.setDate(d.getDate() - i); return d.toISOString().split('T')[0]; }).reverse(); return { labels: d7, datasets: [{ label: 'Reports', data: d7.map(d => citizenReports.filter(r => r.created_at && r.created_at.startsWith(d)).length), borderColor: '#CC0000', backgroundColor: 'rgba(204,0,0,0.1)', fill: true, tension: 0.4 }] }; };
  const statusChartData = () => ({ labels: ['Resolved', 'Pending'], datasets: [{ data: [citizenReports.filter(r => r.status === 'Resolved').length, citizenReports.filter(r => r.status === 'Pending').length], backgroundColor: ['#27ae60', '#e74c3c'] }] });
  const violationChartData = () => { const c = {}; fines.forEach(f => { c[f.violation_type] = (c[f.violation_type] || 0) + 1; }); return { labels: Object.keys(c).slice(0, 6), datasets: [{ label: 'Fines', data: Object.values(c).slice(0, 6), backgroundColor: '#e67e22' }] }; };
  const urgencyChartData = () => ({ labels: ['High', 'Medium', 'Low'], datasets: [{ label: 'Reports', data: [citizenReports.filter(r => r.urgency === 'High').length, citizenReports.filter(r => r.urgency === 'Medium').length, citizenReports.filter(r => r.urgency === 'Low').length], backgroundColor: ['#e74c3c', '#e67e22', '#27ae60'] }] });

  const accountabilityScore = mpStats.total > 0 ? ((mpStats.resolved / mpStats.total) * 100).toFixed(1) : 0;
  const scoreColor = accountabilityScore >= 70 ? '#27ae60' : accountabilityScore >= 40 ? '#e67e22' : '#e74c3c';

  const inputStyle = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '10px' };

  return (
    <div style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>

      {!isOnline && <div style={{ backgroundColor: '#e74c3c', color: 'white', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>📵 Offline — reports will auto-submit when you reconnect{offlineQueue.length > 0 && ` (${offlineQueue.length} queued)`}</div>}
      {isOnline && offlineQueue.length > 0 && <div style={{ backgroundColor: '#27ae60', color: 'white', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>✅ Back online — submitting {offlineQueue.length} queued report(s)…</div>}

      <div style={{ backgroundColor: 'white', padding: '20px', textAlign: 'center', borderBottom: `3px solid ${ghanaRed}` }}>
        <h1><span style={{ color: ghanaBlack }}>CivicConnect</span><span style={{ color: ghanaGreen }}> Ghana</span></h1>
        <p>National Civic Technology Platform</p>
        <p style={{ color: ghanaRed, fontWeight: 'bold' }}>One platform. Four portals. Real accountability for Ghana.</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', backgroundColor: 'white', padding: '10px', flexWrap: 'wrap' }}>
        {['citizen', 'police', 'court', 'mp', 'employee', 'admin'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '10px 15px', backgroundColor: activeTab === tab ? ghanaGreen : '#e0e0e0', color: activeTab === tab ? 'white' : ghanaBlack, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
            {tab === 'citizen' && '📝 Citizen'}{tab === 'police' && '🚔 Police'}{tab === 'court' && '⚖️ Court'}{tab === 'mp' && '🏛️ MP'}{tab === 'employee' && '👥 Employee'}{tab === 'admin' && '🔐 Admin'}
          </button>
        ))}
      </div>

      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* ==================== CITIZEN TAB ==================== */}
        {activeTab === 'citizen' && (
          <div>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', marginBottom: '20px' }}>
              <h2 style={{ color: ghanaGreen }}>Submit a Community Report</h2>
              <p style={{ backgroundColor: '#e8f4f8', padding: '10px', borderRadius: '8px' }}>💰 <strong>You will receive 5 CREDITS when resolved!</strong></p>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold' }}>Region *</label>
                <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} style={{ ...inputStyle, marginTop: '5px' }}>
                  <option value="">Select region</option>{ghanaRegions.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold' }}>Issue Type *</label>
                <select value={issueType} onChange={(e) => setIssueType(e.target.value)} style={{ ...inputStyle, marginTop: '5px' }}>
                  <option>Drainage / Flooding</option><option>Wrong Parking</option><option>Road Damage</option>
                  <option>Littering / Dumping</option><option>Water Supply</option><option>Other</option>
                </select>
              </div>

              {issueType === 'Other' && <input type="text" value={otherExplanation} onChange={(e) => setOtherExplanation(e.target.value)} placeholder="Specify issue" style={inputStyle} />}

              {/* URGENCY SELECTOR */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold' }}>Urgency Level *</label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  {['Low', 'Medium', 'High'].map(level => (
                    <button key={level} type="button" onClick={() => setUrgencyLevel(level)}
                      style={{ flex: 1, padding: '10px', border: `2px solid ${urgencyLevel === level ? (level === 'High' ? '#e74c3c' : level === 'Medium' ? '#e67e22' : '#27ae60') : '#ddd'}`, backgroundColor: urgencyLevel === level ? (level === 'High' ? '#ffeaea' : level === 'Medium' ? '#fff3e0' : '#e8f8f0') : 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', color: level === 'High' ? '#e74c3c' : level === 'Medium' ? '#e67e22' : '#27ae60' }}>
                      {level === 'High' ? '🔴 High' : level === 'Medium' ? '🟡 Medium' : '🟢 Low'}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {urgencyLevel === 'High' && '⚠️ High urgency — prioritised by MPs and flagged in all dashboards'}
                  {urgencyLevel === 'Medium' && 'Medium urgency — standard processing'}
                  {urgencyLevel === 'Low' && 'Low urgency — will be addressed in order'}
                </p>
              </div>

              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3" placeholder="Describe the problem *" style={{ ...inputStyle }} />

              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold' }}>Your Location</label>
                <button onClick={detectCitizenLocation} disabled={isLocating} style={{ backgroundColor: '#2980b9', color: 'white', padding: '10px', width: '100%', margin: '8px 0', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{isLocating ? 'Detecting...' : '📍 Detect My Current Location'}</button>
                <div style={{ height: '300px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${ghanaGreen}`, marginBottom: '10px' }}>
                  <MapContainer center={mapCenter} zoom={14} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <CitizenLocationMarker setLocationLatLng={setLocationLatLng} setLocationLat={setLocationLat} setLocationLng={setLocationLng} setLocationName={setLocationName} />
                  </MapContainer>
                </div>
                <input type="text" value={locationName} onChange={(e) => setLocationName(e.target.value)} placeholder="Or type location manually (e.g., Tano North, Ahafo Region)" style={inputStyle} />
                {locationName && <p style={{ fontSize: '12px', color: ghanaGreen }}>📍 {locationName}</p>}
              </div>

              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="Phone Number * (used to track your report)" style={inputStyle} />

              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold' }}>Photos (Minimum 2 required)</label>
                {photoFiles.map((_, index) => (
                  <div key={index} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                      <button type="button" onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'image/*'; inp.capture = 'environment'; inp.onchange = (e) => handlePhotoChange(e, index); inp.click(); }} style={{ flex: 1, backgroundColor: '#2980b9', color: 'white', padding: '8px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}>📸 Camera</button>
                      <label style={{ flex: 1, backgroundColor: '#666', color: 'white', padding: '8px', textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}>📁 Gallery<input type="file" accept="image/*" onChange={(e) => handlePhotoChange(e, index)} style={{ display: 'none' }} /></label>
                    </div>
                    {index >= 2 && <button onClick={() => removePhotoField(index)} style={{ backgroundColor: '#e74c3c', color: 'white', padding: '8px 12px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>✕</button>}
                    {photoPreviews[index] && <img src={photoPreviews[index]} alt="Preview" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }} />}
                  </div>
                ))}
                <button type="button" onClick={addPhotoField} style={{ backgroundColor: '#2980b9', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>+ Add Another Photo</button>
                {photoFiles.filter(f => f.file).length < 2 && <p style={{ fontSize: '12px', color: '#e74c3c', marginTop: '5px' }}>⚠ At least 2 photos required</p>}
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold' }}>Video (Optional)</label>
                <input id="videoInput" type="file" accept="video/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setSelectedVideo(f); setVideoPreview(URL.createObjectURL(f)); } }} style={{ width: '100%' }} />
                {videoPreview && <video src={videoPreview} controls style={{ maxWidth: '100%', maxHeight: '150px', marginTop: '10px' }} />}
              </div>

              <button onClick={handleCitizenSubmit} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', width: '100%', fontWeight: 'bold', cursor: 'pointer', border: 'none', borderRadius: '8px' }}>
                {isOnline ? 'Submit Report' : '📵 Save Report (Offline Mode)'}
              </button>
            </div>
<GhanaHeatmap reports={citizenReports} title="See Issues Across Ghana" showList={false} />

            {/* TRACK YOUR REPORT */}
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px' }}>
              <h2 style={{ color: ghanaGreen }}>🔍 Track Your Report</h2>
              <p style={{ color: '#666', marginBottom: '15px' }}>Enter the phone number you used when submitting to check your report status — no login required.</p>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <input type="tel" value={trackPhone} onChange={(e) => setTrackPhone(e.target.value)} placeholder="Your phone number" style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                <button onClick={handleTrackReport} disabled={trackLoading} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '10px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>{trackLoading ? '...' : 'Track'}</button>
              </div>
              {trackResults.map((r, i) => {
                const days = daysSince(r.created_at);
                return (
                  <div key={i} style={{ border: `2px solid ${r.status === 'Resolved' ? '#27ae60' : '#e67e22'}`, padding: '15px', borderRadius: '8px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <strong style={{ fontSize: '13px' }}>{r.report_id}</strong>
                      <span style={{ backgroundColor: r.status === 'Resolved' ? '#27ae60' : '#e67e22', color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '12px' }}>{r.status}</span>
                    </div>
                    <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Issue:</strong> {r.issue_type} {urgencyBadge(r.urgency)}</p>
                    <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Location:</strong> {r.location}</p>
                    <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Constituency:</strong> {r.constituency}</p>
                    <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Submitted:</strong> {new Date(r.created_at).toLocaleDateString()}</p>
                    {r.status === 'Pending' && <p style={{ margin: '4px 0', fontSize: '13px', color: days > 14 ? '#e74c3c' : '#e67e22', fontWeight: 'bold' }}>{days > 14 ? `🔴 Overdue — ${days} days (target: 14 days)` : `⏱️ Waiting ${days} day(s)`}</p>}
                    {r.status === 'Resolved' && r.resolution_note && <p style={{ margin: '4px 0', fontSize: '13px', color: '#27ae60' }}><strong>Resolution:</strong> {r.resolution_note}</p>}
                    {r.status === 'Resolved' && <p style={{ margin: '4px 0', fontSize: '13px', color: '#27ae60' }}>✅ Resolved {new Date(r.resolved_at).toLocaleDateString()} — 5 credits awarded!</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ==================== POLICE TAB ==================== */}
        {activeTab === 'police' && (
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px' }}>
            {!policeLoggedIn ? (
              <div>
                <h2 style={{ color: ghanaGreen }}>Police Portal</h2>
                <p style={{ backgroundColor: '#e8f4f8', padding: '10px', borderRadius: '8px' }}>🔐 Authorised personnel only.</p>
                <input type="password" value={policePassword} onChange={(e) => setPolicePassword(e.target.value)} placeholder="Access Code" style={inputStyle} />
                <input type="text" value={policeOfficerName} onChange={(e) => setPoliceOfficerName(e.target.value)} placeholder="Your Name *" style={inputStyle} />
                <button onClick={() => { if (policePassword === 'Police2026' && policeOfficerName) { setPoliceLoggedIn(true); alert('Access granted'); } else alert('Wrong access code'); }} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', border: 'none', borderRadius: '8px', cursor: 'pointer', width: '100%' }}>Login</button>
              </div>
            ) : (
              <div>
                <h2 style={{ color: ghanaGreen }}>Police Portal</h2>
                <p>Logged in as: <strong>{policeOfficerName}</strong></p>
                <button onClick={() => setPoliceLoggedIn(false)} style={{ backgroundColor: '#e74c3c', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '5px', cursor: 'pointer', marginBottom: '20px' }}>Logout</button>

                <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ color: ghanaGold }}>🔍 Search Citizen</h3>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Name, Ghana Card, or phone number" style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                    <button onClick={handleCitizenSearch} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '10px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Search</button>
                  </div>
                  {searchResults.map((result, idx) => (
                    <div key={idx} style={{ backgroundColor: result.source === 'Employee' ? '#e8f4e8' : '#e8f4f8', padding: '15px', borderRadius: '8px', marginTop: '10px', borderLeft: `4px solid ${result.source === 'Employee' ? '#27ae60' : '#2980b9'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <strong style={{ color: result.source === 'Employee' ? '#27ae60' : '#2980b9' }}>{result.source}</strong>
                        {result.source === 'Employee' && <span style={{ backgroundColor: '#27ae60', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '10px' }}>Verified</span>}
                      </div>
                      {result.source === 'Employee' ? (
                        <div>
                          <p style={{ margin: '3px 0' }}><strong>👤 Name:</strong> {result.citizen_name || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>🪪 Ghana Card:</strong> {result.ghana_card || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>📞 Phone:</strong> {result.phone_number || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>📍 Home:</strong> {result.location_home || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>🗺️ GPS:</strong> {result.location_lat && result.location_lng ? `${result.location_lat}, ${result.location_lng}` : 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>🔤 What3Words:</strong> {result.what3words || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>🏠 Region:</strong> {result.region || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>🏛️ District:</strong> {result.district || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>🏘️ Community:</strong> {result.community || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>💼 Occupation:</strong> {result.occupation || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>🎂 DOB:</strong> {result.date_of_birth || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>⚥ Gender:</strong> {result.gender || 'N/A'}</p>
                          <p style={{ margin: '3px 0' }}><strong>👨‍👩‍👧 Next of Kin:</strong> {result.next_of_kin_name || 'N/A'} {result.next_of_kin_phone && `(${result.next_of_kin_phone})`}</p>
                          {result.photo_url && <div style={{ marginTop: '8px' }}><strong>📷 Photo:</strong><br /><img src={result.photo_url} alt="Citizen" style={{ maxWidth: '200px', borderRadius: '8px', marginTop: '5px' }} /></div>}
                        </div>
                      ) : (
                        <div>
                          <p><strong>Name:</strong> {result.name}</p><p><strong>Ghana Card:</strong> {result.gh_card}</p>
                          <p><strong>Phone:</strong> {result.phone}</p><p><strong>Address:</strong> {result.address}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ color: ghanaGold }}>🔍 Number Plate Lookup</h3>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input type="text" value={plateQuery} onChange={(e) => setPlateQuery(e.target.value)} placeholder="e.g., GR-2847-24" style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                    <button onClick={handlePlateLookup} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '10px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Lookup</button>
                  </div>
                  {plateResult && <div style={{ backgroundColor: '#e8f4f8', padding: '15px', borderRadius: '8px' }}><p><strong>Plate:</strong> {plateResult.plate}</p><p><strong>Owner:</strong> {plateResult.owner}</p><p><strong>Make:</strong> {plateResult.make}</p><p><strong>Outstanding Fines:</strong> {plateResult.outstandingFines || plateResult.fine_amount}</p></div>}
                </div>

                <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                  <h3 style={{ color: ghanaRed }}>🚨 Report Criminal Activity & Issue Fine</h3>
                  <select value={crimeType} onChange={(e) => setCrimeType(e.target.value)} style={inputStyle}><option>Theft/Robbery</option><option>Assault</option><option>Drug-related</option><option>Domestic Violence</option><option>Fraud/Scam</option><option>Vandalism</option><option>Trespassing</option><option>Suspicious Activity</option><option>Other</option></select>
                  {crimeType === 'Other' && <input type="text" value={crimeOtherText} onChange={(e) => setCrimeOtherText(e.target.value)} placeholder="Specify crime" style={inputStyle} />}
                  <textarea value={crimeDescription} onChange={(e) => setCrimeDescription(e.target.value)} rows="2" placeholder="Description *" style={inputStyle} />
                  <button onClick={detectCrimeLocation} style={{ backgroundColor: '#2980b9', color: 'white', padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer', width: '100%', marginBottom: '10px' }}>📍 Detect Location</button>
                  <div style={{ height: '300px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${ghanaGreen}`, marginBottom: '10px' }}>
                    <MapContainer center={[5.6037, -0.1870]} zoom={13} style={{ height: '100%', width: '100%' }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                      <PoliceLocationMarker setLocationLat={setCrimeLocationLat} setLocationLng={setCrimeLocationLng} setLocationName={setCrimeLocationName} setLocation={setCrimeLocation} />
                    </MapContainer>
                  </div>
                  {crimeLocationName && <p style={{ fontSize: '12px', color: ghanaGreen, marginBottom: '10px' }}>📍 {crimeLocationName}</p>}
                  <input type="text" value={crimeLocation} onChange={(e) => setCrimeLocation(e.target.value)} placeholder="Or type location manually" style={inputStyle} />
                  <select value={crimeRegion} onChange={(e) => setCrimeRegion(e.target.value)} style={inputStyle}><option>Select region</option>{ghanaRegions.map(r => <option key={r}>{r}</option>)}</select>
                  <input type="text" value={suspectName} onChange={(e) => setSuspectName(e.target.value)} placeholder="Suspect Name" style={inputStyle} />
                  <input type="text" value={suspectPlate} onChange={(e) => setSuspectPlate(e.target.value)} placeholder="Suspect Vehicle Plate" style={inputStyle} />
                  <input type="number" value={fineAmountCrime} onChange={(e) => setFineAmountCrime(parseInt(e.target.value) || 0)} placeholder="Fine Amount (GH¢)" style={inputStyle} />
                  <input type="text" value={fineReason} onChange={(e) => setFineReason(e.target.value)} placeholder="Reason for Fine" style={inputStyle} />
                  <input type="date" value={courtDate} onChange={(e) => setCourtDate(e.target.value)} style={inputStyle} />
                  <input id="crimePhotoInput" type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setCrimePhoto(f); setCrimePhotoPreview(URL.createObjectURL(f)); } }} style={{ width: '100%', marginBottom: '10px' }} />
                  {crimePhotoPreview && <img src={crimePhotoPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', marginBottom: '10px' }} />}
                  <button onClick={handleCrimeSubmit} style={{ backgroundColor: ghanaRed, color: 'white', padding: '12px', border: 'none', borderRadius: '8px', cursor: 'pointer', width: '100%' }}>Issue Crime Report & Fine</button>
                </div>

                <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}>
                  <h3 style={{ color: ghanaGold }}>🅿️ Report Parking Violation & Issue Fine</h3>
                  <button onClick={detectParkingLocation} style={{ backgroundColor: '#2980b9', color: 'white', padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer', width: '100%', marginBottom: '10px' }}>📍 Detect Location</button>
                  <div style={{ height: '300px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${ghanaGreen}`, marginBottom: '10px' }}>
                    <MapContainer center={[5.6037, -0.1870]} zoom={13} style={{ height: '100%', width: '100%' }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                      <PoliceLocationMarker setLocationLat={setParkingLocationLat} setLocationLng={setParkingLocationLng} setLocationName={setParkingLocationName} setLocation={setParkingLocation} />
                    </MapContainer>
                  </div>
                  {parkingLocationName && <p style={{ fontSize: '12px', color: ghanaGreen, marginBottom: '10px' }}>📍 {parkingLocationName}</p>}
                  <input type="text" value={parkingLocation} onChange={(e) => setParkingLocation(e.target.value)} placeholder="Or type location manually" style={inputStyle} />
                  <select value={parkingRegion} onChange={(e) => setParkingRegion(e.target.value)} style={inputStyle}><option>Select region</option>{ghanaRegions.map(r => <option key={r}>{r}</option>)}</select>
                  <input type="text" value={parkingNumberPlate} onChange={(e) => setParkingNumberPlate(e.target.value)} placeholder="Number Plate *" style={inputStyle} />
                  <select value={violationType} onChange={(e) => setViolationType(e.target.value)} style={inputStyle}><option>Parking on pavement/sidewalk</option><option>Blocking emergency lane</option><option>Double parking</option><option>No-parking zone</option><option>Blocking driveway</option><option>Speeding</option><option>Other</option></select>
                  {violationType === 'Other' && <input type="text" value={violationOtherText} onChange={(e) => setViolationOtherText(e.target.value)} placeholder="Specify violation" style={inputStyle} />}
                  <input type="number" value={fineAmountParking} onChange={(e) => setFineAmountParking(parseInt(e.target.value) || 0)} style={inputStyle} />
                  <input id="parkingPhotoInput" type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setParkingPhoto(f); setParkingPhotoPreview(URL.createObjectURL(f)); } }} style={{ width: '100%', marginBottom: '10px' }} />
                  {parkingPhotoPreview && <img src={parkingPhotoPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', marginBottom: '10px' }} />}
                  <button onClick={handleParkingSubmit} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', border: 'none', borderRadius: '8px', cursor: 'pointer', width: '100%' }}>Issue Parking Fine</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== COURT TAB ==================== */}
        {activeTab === 'court' && (
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px' }}>
            {!courtLoggedIn ? (
              <div><h2>Court Portal</h2><input type="password" value={courtPassword} onChange={(e) => setCourtPassword(e.target.value)} placeholder="Access Code" style={inputStyle} /><button onClick={() => { if (courtPassword === 'Court2026') setCourtLoggedIn(true); else alert('Wrong'); }} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', width: '100%', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Login</button></div>
            ) : (
              <div>
                <h2>Court Portal</h2>
                <button onClick={() => setCourtLoggedIn(false)} style={{ backgroundColor: '#e74c3c', color: 'white', padding: '5px 10px', marginBottom: '20px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Logout</button>
                <div style={{ backgroundColor: '#f8f9fa', padding: '20px', textAlign: 'center', marginBottom: '20px', borderRadius: '8px' }}>
                  <p><strong>Total Fines:</strong> {fines.length}</p>
                  <p><strong>Revenue:</strong> GH¢ {fines.reduce((s, f) => s + (f.fine_amount || 0), 0)}</p>
                  <p><strong>Unpaid:</strong> {fines.filter(f => f.status === 'Unpaid').length}</p>
                </div>
                {fines.map(fine => (
                  <div key={fine.id} style={{ border: '1px solid #ddd', padding: '15px', marginBottom: '10px', borderRadius: '8px' }}>
                    <p><strong>ID:</strong> {fine.fine_id}</p>
                    <p><strong>Offender:</strong> {fine.offender_name}</p>
                    <p><strong>Violation:</strong> {fine.violation_type}</p>
                    <p><strong>Amount:</strong> GH¢ {fine.fine_amount}</p>
                    <p><strong>Court Date:</strong> {fine.court_date || 'Not scheduled'}</p>
                    <p><strong>Status:</strong> <span style={{ color: fine.status === 'Unpaid' ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>{fine.status}</span></p>
                    <button onClick={() => printFineReceipt(fine)} style={{ backgroundColor: '#2980b9', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>🖨️ Print</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== MP TAB ==================== */}
        {activeTab === 'mp' && (
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px' }}>
            {!mpLoggedIn ? (
              <div>
                <h2 style={{ color: ghanaGreen }}>MP / DCE / MCE Portal</h2>
                <p style={{ backgroundColor: '#e8f4f8', padding: '10px', borderRadius: '8px' }}>🔐 Authorised officials only.</p>
                <input type="password" value={mpPassword} onChange={(e) => setMpPassword(e.target.value)} placeholder="Access Code" style={inputStyle} />
                <button onClick={() => { if (mpPassword === 'ForGhana2026') setMpLoggedIn(true); else alert('Wrong code'); }} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', width: '100%', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Login to MP Portal</button>
              </div>
            ) : (
              <div>
                <h2 style={{ color: ghanaGreen }}>MP / DCE / MCE Dashboard</h2>
                <button onClick={() => setMpLoggedIn(false)} style={{ backgroundColor: '#e74c3c', color: 'white', padding: '5px 10px', marginBottom: '20px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Logout</button>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontWeight: 'bold' }}>Select Your Constituency</label>
                  <select value={selectedConstituency} onChange={(e) => setSelectedConstituency(e.target.value)} style={{ ...inputStyle, marginTop: '5px' }}>
                    <option value="">Select constituency</option>
                    {constituencies.map(c => <option key={c.id} value={c.name}>{c.name} ({c.region})</option>)}
                  </select>
                  <button onClick={loadMPReports} style={{ backgroundColor: '#2980b9', color: 'white', padding: '10px', width: '100%', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Load My Constituency Data</button>
                </div>

                {selectedConstituency && (
                  <>
                  <GhanaHeatmap reports={citizenReports} title="Regional Overview" />

                    {/* ACCOUNTABILITY SCORE */}
                    <div style={{ backgroundColor: '#1a3a5c', color: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>Public Accountability Score</p>
                      <h1 style={{ margin: '8px 0', fontSize: '56px', color: scoreColor }}>{accountabilityScore}%</h1>
                      <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>{mpStats.resolved} of {mpStats.total} reports resolved — {selectedConstituency}</p>
                      <div style={{ backgroundColor: '#2c4a6c', borderRadius: '8px', height: '12px', marginTop: '12px', overflow: 'hidden' }}>
                        <div style={{ backgroundColor: scoreColor, width: `${accountabilityScore}%`, height: '12px', borderRadius: '8px', transition: 'width 0.5s ease' }} />
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: '11px', opacity: 0.6 }}>This score can be published publicly to show governance accountability</p>
                    </div>

                    {/* STATS */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', marginBottom: '20px' }}>
                      {[{ label: 'Total', value: mpStats.total, color: ghanaGreen, bg: '#e8f4f8' }, { label: '🔴 High', value: mpStats.highUrgency, color: ghanaRed, bg: '#ffeaea' }, { label: 'Resolved', value: mpStats.resolved, color: '#27ae60', bg: '#e8f8f0' }, { label: 'Pending', value: mpStats.pending, color: '#e67e22', bg: '#fff3e0' }, { label: 'Credits', value: mpStats.credits, color: '#f39c12', bg: '#fffde7' }].map(s => (
                        <div key={s.label} style={{ backgroundColor: s.bg, padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                          <h3 style={{ margin: 0, color: s.color }}>{s.value}</h3><p style={{ margin: 0, fontSize: '12px' }}>{s.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* CHARTS */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '20px', marginBottom: '20px' }}>
                      <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}>
                        <h3 style={{ textAlign: 'center' }}>Reports by Category</h3>
                        {Object.keys(mpCategoryCounts).length > 0 ? <Pie data={{ labels: Object.keys(mpCategoryCounts), datasets: [{ data: Object.values(mpCategoryCounts), backgroundColor: ['#CC0000','#006B3F','#FFD700','#1a3a5c','#e67e22','#8e44ad','#2980b9','#27ae60'] }] }} /> : <p style={{ textAlign: 'center', color: '#999' }}>No data yet</p>}
                      </div>
                      <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}>
                        <h3 style={{ textAlign: 'center' }}>Reports by Urgency</h3>
                        <Bar data={{ labels: ['High','Medium','Low'], datasets: [{ label: 'Reports', data: [mpReports.filter(r=>r.urgency==='High').length, mpReports.filter(r=>r.urgency==='Medium').length, mpReports.filter(r=>r.urgency==='Low').length], backgroundColor: ['#e74c3c','#e67e22','#27ae60'] }] }} />
                      </div>
                    </div>

                    {/* REPORTS LIST */}
                    <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}>
                      <h3>Reports in {selectedConstituency}</h3>
                      {mpReports.length === 0 ? <p>No reports found.</p> : mpReports.map(r => {
                        const days = daysSince(r.created_at);
                        const overdue = r.status === 'Pending' && days > 14;
                        return (
                          <div key={r.id} style={{ border: `2px solid ${overdue ? '#e74c3c' : r.status === 'Resolved' ? '#27ae60' : '#ddd'}`, padding: '15px', marginBottom: '12px', borderRadius: '8px', backgroundColor: 'white' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <strong style={{ fontSize: '13px' }}>{r.report_id}</strong>
                              <div>{urgencyBadge(r.urgency)}<span style={{ backgroundColor: r.status === 'Resolved' ? '#27ae60' : '#e67e22', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', marginLeft: '4px' }}>{r.status}</span></div>
                            </div>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Issue:</strong> {r.issue_type}</p>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>{r.description}</p>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Location:</strong> {r.location}</p>
                            {/* RESPONSE TIME TRACKER */}
                            {r.status === 'Pending' && (
                              <div style={{ backgroundColor: overdue ? '#ffeaea' : '#fff3e0', padding: '8px 12px', borderRadius: '6px', marginTop: '8px' }}>
                                <p style={{ margin: 0, fontSize: '12px', fontWeight: 'bold', color: overdue ? '#e74c3c' : '#e67e22' }}>
                                  {overdue ? `🔴 OVERDUE — ${days} days (target: 14 days)` : `⏱️ Open for ${days} day(s) — ${14 - days} day(s) remaining`}
                                </p>
                              </div>
                            )}
                            {r.photo_url && <img src={r.photo_url} alt="Report" style={{ maxWidth: '150px', borderRadius: '6px', marginTop: '8px' }} />}
                            {r.status !== 'Resolved' && (
                              <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
                                <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Resolution Note (describe what action was taken) *</label>
                                <textarea id={`resNote_${r.report_id}`} rows="2" placeholder="e.g., Road repair team dispatched, pothole filled on 10 June 2026" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '8px', fontSize: '13px' }} />
                                <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Upload "After" Photo (proof of resolution) *</label>
                                <input type="file" id={`afterPhoto_${r.report_id}`} accept="image/*" style={{ width: '100%', padding: '6px', marginBottom: '8px' }} />
                                <button onClick={() => resolveReport(r.report_id, r.phone_number)} style={{ backgroundColor: '#27ae60', color: 'white', padding: '10px', border: 'none', borderRadius: '5px', cursor: 'pointer', width: '100%', fontWeight: 'bold' }}>✓ Mark as Resolved (Award 5 Credits)</button>
                              </div>
                            )}
                            {r.status === 'Resolved' && (
                              <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#e8f8f0', borderRadius: '6px' }}>
                                {r.resolution_note && <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#27ae60' }}><strong>Note:</strong> {r.resolution_note}</p>}
                                <p style={{ margin: 0, fontSize: '12px', color: '#27ae60' }}>✅ Resolved on {r.resolved_at ? new Date(r.resolved_at).toLocaleDateString() : 'N/A'}</p>
                                {r.after_photo_url && <img src={r.after_photo_url} alt="After" style={{ maxWidth: '150px', borderRadius: '6px', marginTop: '6px' }} />}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== EMPLOYEE TAB ==================== */}
        {activeTab === 'employee' && (
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px' }}>
            {!employeeLoggedIn ? (
              <div><h2>Employee Portal</h2><input type="password" value={employeePassword} onChange={(e) => setEmployeePassword(e.target.value)} placeholder="Access Code" style={inputStyle} /><input type="text" value={employeeName} onChange={(e) => setEmployeeName(e.target.value)} placeholder="Your Name" style={inputStyle} /><button onClick={() => { if (employeePassword === 'Employee2026' && employeeName) setEmployeeLoggedIn(true); else alert('Wrong'); }} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', width: '100%', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Login</button></div>
            ) : (
              <div>
                <h2>Citizen Data Collection</h2>
                <p>Logged in: <strong>{employeeName}</strong></p>
                <button onClick={() => setEmployeeLoggedIn(false)} style={{ backgroundColor: '#e74c3c', color: 'white', padding: '5px 10px', marginBottom: '20px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Logout</button>
                <input type="text" value={citizenName} onChange={(e) => setCitizenName(e.target.value)} placeholder="Citizen Name" style={inputStyle} />
                <input type="text" value={ghanaCard} onChange={(e) => setGhanaCard(e.target.value)} placeholder="Ghana Card" style={inputStyle} />
                <input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} style={inputStyle} />
                <select value={gender} onChange={(e) => setGender(e.target.value)} style={inputStyle}><option>Gender</option><option>Male</option><option>Female</option></select>
                <input type="tel" value={employeePhone} onChange={(e) => setEmployeePhone(e.target.value)} placeholder="Phone" style={inputStyle} />
                <button onClick={detectEmployeeLocation} style={{ backgroundColor: '#2980b9', color: 'white', padding: '10px', width: '100%', marginBottom: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{employeeIsLocating ? 'Detecting...' : '📍 Detect Location'}</button>
                <div style={{ height: '250px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${ghanaGreen}`, marginBottom: '10px' }}>
                  <MapContainer center={employeeMapCenter} zoom={14} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <EmployeeLocationMarker setLocationLat={setEmployeeLocationLat} setLocationLng={setEmployeeLocationLng} setLocationName={setEmployeeLocationName} setLocationHome={setLocationHome} />
                  </MapContainer>
                </div>
                <div style={{ backgroundColor: '#f0f2f5', padding: '12px', borderRadius: '8px', marginBottom: '10px', fontSize: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" id="employeeConsentCheckbox" />
                    <span>I confirm that I have obtained verbal consent from this citizen as described in the <button type="button" onClick={() => setShowPrivacy(true)} style={{ background: 'none', border: 'none', color: '#006B3F', textDecoration: 'underline', cursor: 'pointer' }}>Privacy Policy</button>.</span>
                  </label>
                </div>
                <input type="text" value={what3words} onChange={(e) => setWhat3words(e.target.value)} placeholder="What3Words" style={inputStyle} />
                <select value={employeeRegion} onChange={(e) => setEmployeeRegion(e.target.value)} style={inputStyle}><option>Region</option>{ghanaRegions.map(r => <option key={r}>{r}</option>)}</select>
                <input type="text" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="District" style={inputStyle} />
                <input type="text" value={community} onChange={(e) => setCommunity(e.target.value)} placeholder="Community" style={inputStyle} />
                <input type="text" value={occupation} onChange={(e) => setOccupation(e.target.value)} placeholder="Occupation" style={inputStyle} />
                <input type="text" value={nextOfKinName} onChange={(e) => setNextOfKinName(e.target.value)} placeholder="Next of Kin" style={inputStyle} />
                <input type="text" value={nextOfKinPhone} onChange={(e) => setNextOfKinPhone(e.target.value)} placeholder="Next of Kin Phone" style={inputStyle} />
                <input id="employeePhotoInput" type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setEmployeePhoto(f); setEmployeePhotoPreview(URL.createObjectURL(f)); } }} style={{ width: '100%', marginBottom: '10px' }} />
                {employeePhotoPreview && <img src={employeePhotoPreview} alt="Preview" style={{ maxWidth: '150px', borderRadius: '8px', marginBottom: '10px' }} />}
                <button onClick={handleEmployeeSubmit} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', width: '100%', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Submit</button>
              </div>
            )}
          </div>
        )}

        {/* ==================== ADMIN TAB ==================== */}
        {activeTab === 'admin' && (
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px' }}>
            <h2 style={{ color: ghanaGreen }}>Admin Portal</h2>
            <p style={{ backgroundColor: '#1a3a5c', color: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>🔐 Confidential - Founder Only</p>
            {!adminLoggedIn ? (
              <div>
                <input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="Admin Password" style={inputStyle} />
                <button onClick={() => { if (adminPassword === 'CivicConnect2026') setAdminLoggedIn(true); else alert('Wrong password'); }} style={{ backgroundColor: ghanaGreen, color: 'white', padding: '12px', width: '100%', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Login to Admin Portal</button>
              </div>
            ) : (
              <div>
                <button onClick={() => setAdminLoggedIn(false)} style={{ backgroundColor: '#e74c3c', color: 'white', padding: '5px 10px', marginBottom: '20px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Logout</button>

                <h3>Quick Overview</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '20px' }}>
                  <div style={{ backgroundColor: '#e8f4f8', padding: '15px', borderRadius: '8px', textAlign: 'center' }}><h3 style={{ margin: 0, color: ghanaGreen }}>{citizenReports.length}</h3><p style={{ margin: 0 }}>Total Reports</p></div>
                  <div style={{ backgroundColor: '#ffeaea', padding: '15px', borderRadius: '8px', textAlign: 'center' }}><h3 style={{ margin: 0, color: ghanaRed }}>{citizenReports.filter(r => r.urgency === 'High').length}</h3><p style={{ margin: 0 }}>🔴 High Urgency</p></div>
                  <div style={{ backgroundColor: '#e8f4f8', padding: '15px', borderRadius: '8px', textAlign: 'center' }}><h3 style={{ margin: 0, color: '#2980b9' }}>{fines.length}</h3><p style={{ margin: 0 }}>Fines Issued</p></div>
                  <div style={{ backgroundColor: '#e8f4f8', padding: '15px', borderRadius: '8px', textAlign: 'center' }}><h3 style={{ margin: 0, color: ghanaGold }}>GH¢ {fines.reduce((s,f)=>s+(f.fine_amount||0),0)}</h3><p style={{ margin: 0 }}>Revenue</p></div>
                </div>

                {/* FILTERS */}
                <h3>🔽 Filter Reports</h3>
                <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '10px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Region</label>
                      <select value={adminFilterRegion} onChange={(e) => { setAdminFilterRegion(e.target.value); setAdminFilterConstituency('All'); }} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}>
                        <option value="All">All Regions</option>{ghanaRegions.map(r => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Constituency</label>
                      <select value={adminFilterConstituency} onChange={(e) => setAdminFilterConstituency(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}>
                        {adminConstituencies.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Date From</label>
                      <input type="date" value={adminFilterDateFrom} onChange={(e) => setAdminFilterDateFrom(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Date To</label>
                      <input type="date" value={adminFilterDateTo} onChange={(e) => setAdminFilterDateTo(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#666' }}>Showing <strong>{filteredReports.length}</strong> of {citizenReports.length} reports</span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => { setAdminFilterRegion('All'); setAdminFilterConstituency('All'); setAdminFilterDateFrom(''); setAdminFilterDateTo(''); }} style={{ backgroundColor: '#95a5a6', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Clear</button>
                      <button onClick={exportFilteredCSV} style={{ backgroundColor: '#27ae60', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>📥 Export CSV ({filteredReports.length})</button>
                    </div>
                  </div>
                </div>

                {/* REGION TABLE */}
                <h3>Reports by Region</h3>
                <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ backgroundColor: ghanaGreen, color: 'white' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Region</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Reports</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>🔴 High</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Resolved</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Percentage</th>
                    </tr></thead>
                    <tbody>
                      {ghanaRegions.map(region => {
                        const rR = citizenReports.filter(r => (r.selected_region || r.region) === region);
                        const count = rR.length; const high = rR.filter(r => r.urgency === 'High').length; const resolved = rR.filter(r => r.status === 'Resolved').length;
                        const pct = citizenReports.length > 0 ? ((count / citizenReports.length) * 100).toFixed(1) : 0;
                        return <tr key={region} style={{ borderBottom: '1px solid #ddd' }}>
                          <td style={{ padding: '10px' }}><strong>{region}</strong></td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>{count}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}><span style={{ color: '#e74c3c', fontWeight: 'bold' }}>{high}</span></td>
                          <td style={{ padding: '10px', textAlign: 'center' }}><span style={{ color: '#27ae60', fontWeight: 'bold' }}>{resolved}</span></td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>
                            <div style={{ backgroundColor: '#e0e0e0', borderRadius: '10px', height: '20px', width: '150px', margin: '0 auto' }}>
                              <div style={{ backgroundColor: ghanaGreen, width: `${pct}%`, height: '20px', borderRadius: '10px', textAlign: 'center', color: 'white', fontSize: '11px', lineHeight: '20px' }}>{pct}%</div>
                            </div>
                          </td>
                        </tr>;
                      })}
                    </tbody>
                  </table>
                </div>

                {/* TOP CONSTITUENCIES */}
                <h3>Top 10 Constituencies</h3>
                <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ backgroundColor: ghanaGold, color: '#333' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Constituency</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Region</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Reports</th>
                      <th style={{ padding: '10px', textAlign: 'center' }}>Resolution %</th>
                    </tr></thead>
                    <tbody>
                      {(() => {
                        const cc = {}; const cR = {}; const cRs = {};
                        citizenReports.forEach(r => { const c = r.constituency || 'Unknown'; cc[c] = (cc[c] || 0) + 1; if (!cR[c]) cR[c] = r.region || r.selected_region || 'Unknown'; if (r.status === 'Resolved') cRs[c] = (cRs[c] || 0) + 1; });
                        return Object.entries(cc).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([c, count]) => {
                          const res = cRs[c] || 0; const pct = ((res / count) * 100).toFixed(0);
                          return <tr key={c} style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '10px' }}><strong>{c}</strong></td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{cR[c]}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{count}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}><span style={{ color: pct >= 70 ? '#27ae60' : pct >= 40 ? '#e67e22' : '#e74c3c', fontWeight: 'bold' }}>{pct}%</span></td>
                          </tr>;
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
<GhanaHeatmap reports={citizenReports} title="National Reports Heatmap" />

                {/* CHARTS */}
                <h3>Visualizations</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '20px', marginBottom: '20px' }}>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}><h3 style={{ textAlign: 'center' }}>Reports by Category</h3><Pie data={categoryChartData()} /></div>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}><h3 style={{ textAlign: 'center' }}>Urgency Distribution</h3><Bar data={urgencyChartData()} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '20px', marginBottom: '20px' }}>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}>
                    <h3 style={{ textAlign: 'center' }}>🗺️ Heatmap — Reports by Region</h3>
                    <Bar data={regionChartData()} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} />
                    <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', marginTop: '4px' }}>Darker = higher report density</p>
                  </div>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}><h3 style={{ textAlign: 'center' }}>Top Constituencies</h3><Bar data={constituencyChartData()} options={{ indexAxis: 'y' }} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '20px', marginBottom: '20px' }}>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}><h3 style={{ textAlign: 'center' }}>Reports Timeline (7 days)</h3><Line data={timelineChartData()} /></div>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px' }}><h3 style={{ textAlign: 'center' }}>Report Status</h3><Doughnut data={statusChartData()} /></div>
                </div>
                <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}><h3 style={{ textAlign: 'center' }}>Fines by Violation Type</h3><Bar data={violationChartData()} /></div>

                <button onClick={exportFilteredCSV} style={{ backgroundColor: '#27ae60', color: 'white', padding: '12px', width: '100%', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '10px', fontWeight: 'bold' }}>
                  📥 Download {adminFilterRegion !== 'All' || adminFilterConstituency !== 'All' || adminFilterDateFrom ? 'Filtered' : 'All'} Reports CSV ({filteredReports.length} records)
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      <div style={{ textAlign: 'center', padding: '20px' }}>
        <button onClick={() => setShowPrivacy(true)} style={{ background: 'none', border: 'none', color: '#006B3F', cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</button>
      </div>

      {showPrivacy && (
        <div onClick={() => setShowPrivacy(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ color: '#006B3F', margin: 0 }}>Privacy Policy</h2>
              <button onClick={() => setShowPrivacy(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
              <p><strong>📌 What we collect:</strong></p>
              <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
                <li><strong>Citizen Portal:</strong> Phone number only (anonymous reporting)</li>
                <li><strong>Employee Portal:</strong> Full name, Ghana Card, DOB, gender, phone, home location, what3words, region, district, community, occupation, next of kin, photograph</li>
              </ul>
              <p><strong>📍 Data Storage:</strong> United States (North Carolina)</p>
              <p><strong>🔒 Data Controller:</strong> Bismark Wiafe Bimpong</p>
              <p><strong>📧 Contact:</strong> ghanacivicconnect@gmail.com</p>
              <p><strong>📋 Your Rights (Ghana Act 843):</strong></p>
              <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
                <li>Request access, correction, or deletion of your data</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p><strong>🗑️ Data Deletion:</strong> Email ghanacivicconnect@gmail.com with your Report ID.</p>
              <p><strong>📢 Consent:</strong> By using CivicConnect Ghana, you consent to data collection as described.</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;